<template>
  <form class="cancel-visit-form" @submit.prevent="cancelVisitAsync">
    <div v-if="companyData" class="cancel-visit-form__view">
      <div class="cancel-visit-form__visit-row">
        <p class="cancel-visit-form__visit-cell">
          <b>Company Name:</b> {{ !groupVisit ? companyData.Plant : "" }}
          <DropDownList
            v-if="groupVisit"
            class="company-dropdown"
            v-model="dropdownCompany"
            :dataItems="otherCompanies"
            text-field="CompanyAndRc"
            data-item-key="CompanyId"
            :popup-settings="{ className: 'modal-popup' }"
          />
        </p>
      </div>
      <div class="cancel-visit-form__visit-row">
        <p class="cancel-visit-form__visit-cell">
          <b>Plant Name:</b> {{ companyData.Plant }}
        </p>
        <p v-if="!hideVisitId" class="cancel-visit-form__visit-cell">
          <b>Visit ID:</b> {{ props.visitId }}
        </p>
      </div>
      <div class="cancel-visit-form__visit-row">
        <p class="cancel-visit-form__visit-cell">
          <b>RFR Name:</b> {{ companyData.Rfr }}
        </p>
        <p class="cancel-visit-form__visit-cell">
          <b>RC Name:</b> {{ companyData.Rc }}
        </p>
      </div>
      <div class="cancel-visit-form__visit-row">
        <p class="cancel-visit-form__visit-cell">
          <b>Plant Contact Name:</b> {{ companyData.PlantContact }}
        </p>
        <p class="cancel-visit-form__visit-cell">
          <b>Visit Range:</b> {{ companyData.DateRange }}
        </p>
      </div>
      <div class="cancel-visit-form__visit-row">
        <p class="cancel-visit-form__visit-cell">
          <b>Plant Contact Email:</b> {{ companyData.Email }}
        </p>
        <p class="cancel-visit-form__visit-cell">
          <b>Plant Contact Phone:</b> {{ companyData.Phone }}
        </p>
      </div>
    </div>
    <div class="cancel-visit-form__comment">
      <h2 class="visit-header">Enter Reason for Cancellation (Required)</h2>
      <textarea
        :rows="3"
        :cols="20"
        :maxlength="5000"
        v-model="comment"
        required
      ></textarea>
    </div>
    <LoadingButton
      class="cancel-visit-form__submit-button"
      type="submit"
      themeColor="error"
      :loading="submitting"
      :disabled="submitted"
      >Submit Cancelletion Request</LoadingButton
    >
  </form>
</template>

<script setup lang="ts">
import type { InspectionVisitOtherCompany } from "@/Domain/InspectionVisitOtherCompany";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import { api } from "@/api";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { useInspectionStore } from "@/stores/useInspectionStore";
import { useMainStore } from "@/stores/useMainStore";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { computed, onBeforeMount, ref, watchEffect } from "vue";
import LoadingButton from "@/shared/ui/inputs/LoadingButton.vue";
import { DropDownList } from "@progress/kendo-vue-dropdowns";

const main = useMainStore();
const notificationStore = useNotificationStore();
const inspectionStore = useInspectionStore();

const emit = defineEmits<{
  (e: "submittingChanged", value: boolean): void;
  (e: "visitCanceled"): void;
}>();

const props = defineProps<{
  visitId: number;
  hideVisitId?: boolean;
}>();

const otherCompanies = ref<InspectionVisitOtherCompany[]>([]);
const dropdownCompany = ref<InspectionVisitOtherCompany>();
const comment = ref<string>("");
const submitting = ref<boolean>(false);
const submitted = ref<boolean>(false);

const companyData = computed<InspectionVisitOtherCompany>(() => {
  return otherCompanies.value[0];
});

const groupVisit = computed<boolean>(() => {
  return otherCompanies.value.length > 1;
});

const getVisitOtherCompaniesAsync = async function () {
  const otherCompaniesPromise = api.get("vCartVisitsOtherCompanies", {
    userName: main.userLogin,
    visitId: Number(props.visitId),
  });

  await handleResponsePromiseAsync(
    otherCompaniesPromise,
    (data) => {
      otherCompanies.value = data;
      dropdownCompany.value = data[0];
    },
    "CancelVisitForm.vue",
    "getVisitOtherCompaniesAsync"
  );
};

const showFailedToCancelVisitNotification = function () {
  notificationStore.notify({
    message: "Failed to cancel visit. Please try again later.",
    type: NotificationTypeEnum.error,
    duration: 5000,
  });
};

const cancelVisitAsync = async function () {
  if (submitting.value || submitted.value) return;
  submitting.value = true;
  const cancelVisitPromise = api.post("CancelVisit", {
    VisitId: props.visitId,
    Comment: comment.value,
  });

  await handleResponsePromiseAsync(
    cancelVisitPromise,
    (data) => {
      if (data) {
        notificationStore.notify({
          message: "Visit has been canceled",
          type: NotificationTypeEnum.success,
          duration: 5000,
        });
        submitted.value = true;
        emit("visitCanceled");
      } else {
        showFailedToCancelVisitNotification();
      }
    },
    "VisitInspectionForm.vue",
    "cancelVisitAsync",
    () => {
      showFailedToCancelVisitNotification();
    }
  );
  submitting.value = false;
};

watchEffect(() => {
  emit("submittingChanged", submitting.value);
});

watchEffect(() => {
  inspectionStore.isGroupVisit = groupVisit.value;
});

onBeforeMount(() => {
  getVisitOtherCompaniesAsync();
});
</script>

<style lang="scss" scoped>
.cancel-visit-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  &__view {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  &__visit-row {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
  }

  &__visit-cell {
    flex: 1 1 50%;

    @include media-breakpoint-up(sm) {
      flex: 1;
    }
  }

  &__comment {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .visit-header {
    background: #efefef;
    padding: 12px;
    font-size: 14px;
    font-weight: bold;
    border-radius: 8px;
  }

  textarea {
    width: 100%;
  }

  &__submit-button {
    width: 200px;
  }

  .company-dropdown {
    max-width: 300px;
  }
}
</style>
